import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTestimonialById, createTestimonial, updateTestimonial } from '../../services/testimonialsService';
import Loader from '../../components/common/Loader';
import Input from '../../components/common/Input';
import RichTextEditor from '../../components/forms/RichTextEditor';
import FileUploader from '../../components/forms/FileUploader';
import Button from '../../components/common/Button';
import { PlayCircleIcon } from '@heroicons/react/24/outline';

// Helper to extract YouTube video ID from various URL formats
function getYouTubeVideoId(url) {
  if (!url) return null;
  const regExp = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|)([\w-]{11})(?:\S+)?/;
  const match = url.match(regExp);
  return (match && match[1].length === 11) ? match[1] : null;
}

// Helper to extract Facebook video ID and create embed URL
function getFacebookVideoEmbed(url) {
  if (!url) return null;
  // Facebook video URLs can be complex, so we'll create a basic embed
  const facebookRegex = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?facebook\.com\/.*\/videos\/(\d+)/;
  const match = url.match(facebookRegex);
  if (match) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&width=500&show_text=false&height=280&appId`;
  }
  return null;
}

function TestimonialFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    source_type: 'manual',
    customer_name: '',
    customer_location: '',
    rating: '',
    review_text: '',
    review_date: new Date().toISOString().split('T')[0],
    video_url: '',
    google_review_url: '',
    thumbnail_asset_id: null,
    google_review_id: '',
    is_featured: false,
    is_active: true,
    display_order: 0,
  });
  const [includeYouTubeVideo, setIncludeYouTubeVideo] = useState(false); 

  useEffect(() => {
    const fetchTestimonial = async () => {
      setLoading(true);
      setError('');
      try {
        if (id) {
          const testimonial = await getTestimonialById(id);
          if (testimonial.source_type === 'google_reviews') {
              setError('Google Reviews testimonials cannot be edited directly.');
              setLoading(false);
              return;
          }
          setFormData({
            source_type: testimonial.source_type || 'manual',
            customer_name: testimonial.customer_name || '',
            customer_location: testimonial.customer_location || '',
            rating: testimonial.rating !== undefined && testimonial.rating !== null ? testimonial.rating.toString() : '',
            review_text: testimonial.review_text || '',
            review_date: testimonial.review_date || new Date().toISOString().split('T')[0],
            video_url: testimonial.video_url || '',
            google_review_url: testimonial.google_review_url || '',
            thumbnail_asset_id: testimonial.thumbnail_asset_id || null,
            google_review_id: testimonial.google_review_id || '',
            is_featured: testimonial.is_featured || false,
            is_active: testimonial.is_active !== undefined ? testimonial.is_active : true,
            display_order: testimonial.display_order || 0,
          });

          if (testimonial.source_type === 'manual' && testimonial.video_url && testimonial.video_url.includes('youtube')) {
            setIncludeYouTubeVideo(true);
          } else {
            setIncludeYouTubeVideo(false);
          }

        } else {
          setFormData({
            source_type: 'manual', 
            customer_name: '', customer_location: '', rating: '', review_text: '',
            review_date: new Date().toISOString().split('T')[0],
            youtube_video_url: '', facebook_video_url: '',
            featured_image_url: '', gallery_images: [], is_featured: false, is_active: true, display_order: 0,
          });
          setIncludeYouTubeVideo(false);
        }
      } catch (err) {
        setError('Failed to load testimonial: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonial();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const dataToSave = { ...formData };
      
      if (dataToSave.rating !== '') {
          dataToSave.rating = parseFloat(dataToSave.rating);
      } else {
          dataToSave.rating = null;
      }

      if (dataToSave.source_type === 'manual') {
        if (!includeYouTubeVideo) {
          dataToSave.video_url = '';
        }
      } else if (dataToSave.source_type === 'youtube') {
        delete dataToSave.rating;
        delete dataToSave.review_text;
        delete dataToSave.review_date;
      } else if (dataToSave.source_type === 'facebook') {
        delete dataToSave.rating;
        delete dataToSave.review_text;
        delete dataToSave.review_date;
      }

      if (id) {
        await updateTestimonial(id, dataToSave);
        alert('Testimonial updated successfully!');
      } else {
        await createTestimonial(dataToSave);
        alert('Testimonial created successfully!');
      }
      navigate('/testimonials');
    } catch (err) {
      setError('Failed to save testimonial: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const youtubeVideoId = getYouTubeVideoId(formData.video_url);
  const youtubeEmbedUrl = youtubeVideoId ? `https://www.youtube.com/embed/${youtubeVideoId}` : '';
  const facebookEmbedUrl = getFacebookVideoEmbed(formData.video_url);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative m-6 shadow-md" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <div className="w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center px-2">
          <div className="w-3 h-3 bg-teal-500 rounded-full mr-3"></div>
          {id ? 'Edit Testimonial' : 'Create New Testimonial'}
        </h2>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        {/* Source Type Selection */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
            Source Type
          </h3>
          <label htmlFor="source_type" className="block text-gray-700 text-sm font-semibold mb-2">Choose Testimonial Source</label>
          <select
            id="source_type"
            name="source_type"
            value={formData.source_type}
            onChange={(e) => {
                setIncludeYouTubeVideo(false);
                setFormData(prev => ({
                    ...prev,
                    video_url: '',
                    [e.target.name]: e.target.value,
                }));
            }}
            className="shadow-sm border border-blue-200 rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            disabled={!!id}
          >
            <option value="manual">Manual Review</option>
            <option value="youtube">YouTube Video (Primary)</option>
            <option value="facebook">Facebook Video (Primary)</option>
          </select>
          {!!id && (
            <p className="text-xs text-gray-600 mt-3 bg-white px-3 py-2 rounded-lg border border-blue-200">
              <span className="font-medium">ℹ️ Note:</span> Source type cannot be changed after creation.
            </p>
          )}
        </div>

        {/* Common Fields */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            Customer Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input 
              label="Customer Name" 
              id="customer_name" 
              name="customer_name" 
              value={formData.customer_name} 
              onChange={handleInputChange} 
              required={formData.source_type === 'manual'} 
              className="border-green-200 focus:border-green-500 focus:ring-green-500"
            />
            <Input 
              label="Customer Location" 
              id="customer_location" 
              name="customer_location" 
              value={formData.customer_location} 
              onChange={handleInputChange} 
              className="border-green-200 focus:border-green-500 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Manual Review Specific Fields */}
        {formData.source_type === 'manual' && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
              Review Details
            </h3>
            <div className="space-y-4">
              <Input 
                label="Rating (1-5)" 
                id="rating" 
                name="rating" 
                type="number" 
                value={formData.rating} 
                onChange={handleInputChange} 
                min="1" 
                max="5" 
                step="0.1" 
                required 
                className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
              />
              <RichTextEditor 
                label="Review Text" 
                value={formData.review_text} 
                onChange={(val) => setFormData(prev => ({ ...prev, review_text: val }))} 
                required 
              />
              <Input 
                label="Review Date" 
                id="review_date" 
                name="review_date" 
                type="date" 
                value={formData.review_date} 
                onChange={handleInputChange} 
                required 
                className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
              />

              <div className="mt-6 p-6 border-2 border-blue-200 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50">
                <label className="inline-flex items-center cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={includeYouTubeVideo}
                    onChange={(e) => setIncludeYouTubeVideo(e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-gray-900 font-semibold text-lg inline-flex items-center">
                      <PlayCircleIcon className="h-6 w-6 mr-2 text-blue-600" /> Link a YouTube Video
                  </span>
                </label>

                {includeYouTubeVideo && (
                  <div className="space-y-4">
                    <Input 
                      label="YouTube Video URL" 
                      id="video_url_manual" 
                      name="video_url" 
                      value={formData.video_url} 
                      onChange={handleInputChange} 
                      placeholder="e.g., https://www.youtube.com/watch?v=VIDEO_ID" 
                      required 
                      className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                    {youtubeEmbedUrl && (
                      <div className="mt-4 rounded-xl overflow-hidden shadow-lg border border-blue-200">
                        <div className="bg-white p-3 border-b border-blue-200">
                          <p className="text-sm text-gray-700 font-medium flex items-center">
                            <PlayCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                            Video Preview
                          </p>
                        </div>
                        <div className="aspect-w-16 aspect-h-9 w-full">
                          <iframe
                            src={youtubeEmbedUrl}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          ></iframe>
                        </div>
                      </div>
                    )}
                    {!youtubeEmbedUrl && formData.video_url && (
                      <p className="text-red-600 text-sm mt-2 bg-red-50 p-3 rounded-lg border border-red-200">
                        ⚠️ Invalid YouTube URL. Please enter a valid YouTube video link.
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-600 mt-3 bg-white px-3 py-2 rounded-lg border border-blue-200">
                  <span className="font-medium">💡 Tip:</span> Use this option if the customer's review is primarily text-based, but also has an associated video.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* YouTube Video (Primary) Specific Fields */}
        {formData.source_type === 'youtube' && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-6 border border-red-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
              YouTube Video Details (Primary Content)
            </h3>
            <Input 
              label="YouTube Video URL" 
              id="video_url_primary" 
              name="video_url" 
              value={formData.video_url} 
              onChange={handleInputChange} 
              placeholder="e.g., https://www.youtube.com/watch?v=VIDEO_ID" 
              required 
              className="border-red-200 focus:border-red-500 focus:ring-red-500"
            />
            {youtubeEmbedUrl && (
              <div className="mt-4 rounded-xl overflow-hidden shadow-lg border border-red-200">
                <div className="bg-white p-3 border-b border-red-200">
                  <p className="text-sm text-gray-700 font-medium flex items-center">
                    <PlayCircleIcon className="h-4 w-4 mr-2 text-red-600" />
                    Video Preview
                  </p>
                </div>
                <div className="aspect-w-16 aspect-h-9 w-full">
                  <iframe
                    src={youtubeEmbedUrl}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </div>
            )}
            {!youtubeEmbedUrl && formData.video_url && (
              <p className="text-red-600 text-sm mt-2 bg-red-50 p-3 rounded-lg border border-red-200">
                ⚠️ Invalid YouTube URL. Please enter a valid YouTube video link.
              </p>
            )}
            <p className="text-xs text-gray-600 mt-3 bg-white px-3 py-2 rounded-lg border border-red-200">
              <span className="font-medium">📹 Note:</span> Select this source type if the testimonial *is* primarily a video, not a text review.
            </p>
          </div>
        )}

        {/* Facebook Video (Primary) Specific Fields */}
        {formData.source_type === 'facebook' && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
              Facebook Video Details (Primary Content)
            </h3>
            <Input 
              label="Facebook Video URL" 
              id="video_url_primary" 
              name="video_url" 
              value={formData.video_url} 
              onChange={handleInputChange} 
              placeholder="e.g., https://www.facebook.com/username/videos/123456789" 
              required 
              className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
            />
            {facebookEmbedUrl && (
              <div className="mt-4 rounded-xl overflow-hidden shadow-lg border border-blue-200">
                <div className="bg-white p-3 border-b border-blue-200">
                  <p className="text-sm text-gray-700 font-medium flex items-center">
                    <PlayCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                    Video Preview
                  </p>
                </div>
                <div className="aspect-w-16 aspect-h-9 w-full">
                  <iframe
                    src={facebookEmbedUrl}
                    title="Facebook video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </div>
            )}
            {!facebookEmbedUrl && formData.video_url && (
              <p className="text-red-600 text-sm mt-2 bg-red-50 p-3 rounded-lg border border-red-200">
                ⚠️ Invalid Facebook video URL. Please enter a valid Facebook video link.
              </p>
            )}
            <p className="text-xs text-gray-600 mt-3 bg-white px-3 py-2 rounded-lg border border-blue-200">
              <span className="font-medium">📱 Note:</span> Select this source type if the testimonial *is* primarily a Facebook video, not a text review.
            </p>
          </div>
        )}

        {/* Global Testimonial Settings */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
            Testimonial Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <Input 
              label="Display Order" 
              id="display_order" 
              name="display_order" 
              type="number" 
              value={formData.display_order} 
              onChange={handleInputChange}
              className="border-amber-200 focus:border-amber-500 focus:ring-amber-500"
            />
            <div className="flex items-center space-x-6 mt-7">
              <label className="inline-flex items-center cursor-pointer bg-white px-4 py-2 rounded-lg border border-amber-200 hover:bg-amber-50 transition-colors">
                <input 
                  type="checkbox" 
                  name="is_featured" 
                  checked={formData.is_featured} 
                  onChange={handleInputChange} 
                  className="form-checkbox h-4 w-4 text-amber-600 rounded focus:ring-amber-500" 
                />
                <span className="ml-2 text-gray-700 font-medium">⭐ Featured</span>
              </label>
              <label className="inline-flex items-center cursor-pointer bg-white px-4 py-2 rounded-lg border border-amber-200 hover:bg-amber-50 transition-colors">
                <input 
                  type="checkbox" 
                  name="is_active" 
                  checked={formData.is_active} 
                  onChange={handleInputChange} 
                  className="form-checkbox h-4 w-4 text-amber-600 rounded focus:ring-amber-500" 
                />
                <span className="ml-2 text-gray-700 font-medium">✅ Active</span>
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 bg-gray-50 p-6 rounded-xl mt-6">
          <Button type="button" onClick={() => navigate('/testimonials')} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white">
            {id ? 'Update Testimonial' : 'Create Testimonial'}
          </Button>
        </div>
      </form>
    </div>
    </div>
  );
}

export default TestimonialFormPage;